import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface FailedAttempt {
  id: string;
  assignment_id: string;
  answers: number[];
  score: number;
  total_questions: number;
  attempted_at: string;
  reviewed_by_admin: boolean;
  seller_id: string;
  seller_name: string;
  module_id: string;
  module_title: string;
}

export const useFailedQuizAttempts = () => {
  const queryClient = useQueryClient();

  const { data: failedAttempts = [], isLoading } = useQuery({
    queryKey: ['failed-quiz-attempts'],
    queryFn: async () => {
      // Get all failed attempts with assignment and module info
      const { data: attempts, error } = await supabase
        .from('training_quiz_attempts')
        .select(`
          id,
          assignment_id,
          answers,
          score,
          total_questions,
          attempted_at,
          reviewed_by_admin,
          training_assignments!inner(
            seller_id,
            module_id,
            training_modules!inner(title)
          )
        `)
        .eq('passed', false)
        .order('attempted_at', { ascending: false });

      if (error) throw error;

      // Get seller profiles
      const sellerIds = [...new Set(attempts.map(a => a.training_assignments.seller_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, nombre')
        .in('user_id', sellerIds);

      const profileMap = new Map(profiles?.map(p => [p.user_id, p.nombre]) || []);

      return attempts.map(attempt => ({
        id: attempt.id,
        assignment_id: attempt.assignment_id,
        answers: attempt.answers as number[],
        score: attempt.score,
        total_questions: attempt.total_questions,
        attempted_at: attempt.attempted_at,
        reviewed_by_admin: attempt.reviewed_by_admin,
        seller_id: attempt.training_assignments.seller_id,
        seller_name: profileMap.get(attempt.training_assignments.seller_id) || 'Desconocido',
        module_id: attempt.training_assignments.module_id,
        module_title: attempt.training_assignments.training_modules.title,
      })) as FailedAttempt[];
    },
  });

  const markAsReviewed = useMutation({
    mutationFn: async (attemptId: string) => {
      const { error } = await supabase
        .from('training_quiz_attempts')
        .update({ reviewed_by_admin: true })
        .eq('id', attemptId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['failed-quiz-attempts'] });
      toast.success('Intento marcado como revisado');
    },
    onError: (error) => {
      toast.error('Error: ' + error.message);
    },
  });

  const unreviewedCount = failedAttempts.filter(a => !a.reviewed_by_admin).length;

  return {
    failedAttempts,
    unreviewedCount,
    isLoading,
    markAsReviewed,
  };
};